import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import SafeRichText from '../components/SafeRichText';
import LoadingSpinner from '../components/LoadingSpinner';
import axios from "axios";
import { useCachedData, useCacheInvalidationListener } from '../utils/useCache';
import { useEffect, useState } from 'react';

function Articles() {
  const API_BASE = process.env.REACT_APP_API_BASE_URL || '/backend/php';
  const { data: articles, loading, error, refetch } = useCachedData(
    '/api/articles',
    () => axios.get(`${API_BASE}/api/articles`).then(res => res.data),
    0 // Disable cache for development
  );

  // State for valid authors
  const [validAuthors, setValidAuthors] = useState([]);
  const [authorsLoading, setAuthorsLoading] = useState(true);

  // Fetch valid authors from the API
  useEffect(() => {
    const fetchAuthors = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/authors`);
        const authorsList = Array.isArray(res.data) ? res.data : (Array.isArray(res.data.data) ? res.data.data : []);
        setValidAuthors(authorsList.map(a => a.name));
      } catch (err) {
        console.error('Failed to fetch authors:', err);
        setValidAuthors([]);
      } finally {
        setAuthorsLoading(false);
      }
    };
    fetchAuthors();
  }, [API_BASE]);

  // Debug logging
  useEffect(() => {
    console.log('[Articles.js] Articles data:', { articles, loading, error, authorsLoading, validAuthors });
  }, [articles, loading, error, authorsLoading, validAuthors]);

  // Listen for cache invalidation events from admin pages and refetch
  useCacheInvalidationListener('/api/articles', refetch);

  // Article icons mapping
  const getArticleIcon = (category, index) => {
    const icons = {
      'insight': '💡',
      'news': '📰',
      'event': '📅',
      'research': '🔬',
      'update': '🔔',
      'default': ['📝', '📚', '📖', '🧠', '🌟', '🧩', '📈', '🗞️'][index % 8]
    };
    const categoryKey = category ? category.toLowerCase() : 'default';
    return icons[categoryKey] || icons['default'];
  };

  // Card animation variants (copied from Services)
  const cardVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.15, duration: 0.6, ease: "easeOut" }
    })
  };

  // Helper to truncate text to a word limit with ellipsis
  const truncateText = (htmlText, wordLimit = 30) => {
    if (!htmlText) return '';
    // Strip HTML tags
    const plainText = htmlText.replace(/<[^>]*>/g, '');
    // Split into words and truncate
    const words = plainText.split(/\s+/).filter(word => word.length > 0);
    if (words.length > wordLimit) {
      return words.slice(0, wordLimit).join(' ') + '...';
    }
    return plainText;
  };

  // Helper to download article file by fetching the backend file endpoint
  const downloadArticle = async (article) => {
  try {
    const API_BASE = (process.env.REACT_APP_API_BASE_URL || '/backend/php');
    // Use article_url if available, otherwise construct the default endpoint
    let fileUrl = article.article_url || `${API_BASE}/api/articles/${article.id}/file`;
    if (fileUrl && fileUrl.startsWith('/')) {
      fileUrl = API_BASE.replace(/\/$/, '') + fileUrl;
    }
    
      const resp = await fetch(fileUrl);
      if (!resp.ok) {
        if (resp.status === 404) {
          alert('No downloadable file is available for this article.');
        } else {
          throw new Error('Failed to download file');
        }
        return;
      }
      const arrayBuffer = await resp.arrayBuffer();
      const contentType = resp.headers.get('content-type') || 'application/octet-stream';
      const blob = new Blob([arrayBuffer], { type: contentType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const cd = resp.headers.get('content-disposition');
      let filename = article.title?.replace(/[^a-z0-9.-]/gi, '_') + '.pdf';
      if (cd) {
        // Try RFC5987: filename*=UTF-8''encoded
        let match = /filename\*=UTF-8''([^;\n]+)/i.exec(cd);
        if (match && match[1]) {
          filename = decodeURIComponent(match[1].trim().replace(/"/g, ''));
        } else {
          // Fallback: filename="name.ext" or filename=name.ext
          match = /filename="?([^";]+)"?/i.exec(cd);
          if (match && match[1]) filename = match[1].trim();
        }
      }
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      console.error('Download failed', err);
      alert('Failed to download article file.');
    }
  };

  return (
    <div className="w-full max-w-8xl rounded-lg sm:rounded-xl md:rounded-2xl shadow-xl p-2 sm:p-4 md:p-8 text-base sm:text-lg overflow-x-auto" style={{ background: 'rgba(245, 247, 255, 0.85)', backdropFilter: 'blur(16px)' }}>
      <div className="min-h-screen relative overflow-hidden">
        <div className="fixed inset-0 bg-slate-50" />

        <div className="relative z-10 px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
          <div className="max-w-7xl mx-auto container">
            {/* Main Title */}
            <motion.h1 
              className="text-5xl font-extrabold mb-12 text-center text-black tracking-wide" 
              style={{textShadow: '0 6px 32px rgba(0,0,0,0.45)'}}
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <span className="text-[#3a8b9e]">Publications</span>
            </motion.h1>

        {/* Loading State */}
        {loading && (
          <motion.div 
            className="text-center mb-16 flex justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <LoadingSpinner message="Loading publications..." bgColor="bg-transparent" spinnerColor="border-[#73ab40]" py={0} />
          </motion.div>
        )}

        {/* Error State */}
        {error && (
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-flex items-center px-6 py-3 bg-blue-50 rounded-full shadow-lg border border-blue-200">
              <p className="text-blue-600 font-medium">No Publications available right now. Check back soon!</p>
            </div>
          </motion.div>
        )}

        {/* Articles Grid */}
        {!loading && !error && articles && articles.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
            {articles.sort((a, b) => {
              const dateA = new Date(a.date || 0);
              const dateB = new Date(b.date || 0);
              return dateA - dateB;
            }).map((article, idx) => (
              <motion.div
                key={idx}
                role="button"
                tabIndex={0}
                onClick={() => downloadArticle(article)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); downloadArticle(article); } }}
                className="relative bg-white/10 backdrop-blur-xl rounded-3xl pt-10 px-6 pb-6 flex flex-col items-center text-center shadow-xl border border-white/10 transition-all duration-500 h-[420px] w-full group hover:shadow-2xl cursor-pointer overflow-hidden"
                custom={idx}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={cardVariants}
                whileHover={{
                  y: -20,
                  scale: 1.05,
                  boxShadow: "0 20px 60px rgba(0, 0, 0, 0.2)"
                }}
              >
                {/* Decorative corner accent */}
                <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-bl from-primary/20 to-transparent rounded-bl-3xl rounded-tr-3xl"></div>
                {/* Article icon removed per request */}
                {/* Article Title */}
                <h2 className="text-xl md:text-2xl font-bold text-black mb-3 drop-shadow-sm group-hover:text-primary-500 transition-colors duration-300">
                  {article.title}
                </h2>
                {/* Article Author */}
                {article.author && validAuthors.includes(article.author) && (
                  <div className="bg-primary/10 text-primary-500 px-3 py-1 rounded-full font-medium text-base mb-4 border border-primary/30">
                    By {article.author}
                  </div>
                )}
                {/* Article Date */}
                {article.date && (
                  <div className="bg-primary/10 text-primary-500 px-3 py-1 rounded-full font-medium text-base mb-4 border border-primary/30">
                    {new Date(article.date).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'short', 
                      day: 'numeric' 
                    })}
                  </div>
                )}
                {/* Article Excerpt */}
                <div className="text-black text-sm sm:text-base md:text-base text-center leading-relaxed max-h-[100px] overflow-hidden flex-shrink-0">
                  <div className="line-clamp-3">
                    {truncateText(article.content, 15)}
                  </div>
                </div>
                <div className="flex-grow" />
                {/* Read more indicator - Static position, hidden until hover */}
                <motion.div 
                  className="mt-4 text-primary-500 font-medium text-base opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex-shrink-0"
                  initial={{ y: 10 }}
                  whileHover={{ y: 0 }}
                >
                  Download →
                </motion.div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && (!articles || articles.length === 0) && (
          <motion.div 
            className="text-center py-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="bg-white/10 rounded-2xl shadow-lg border border-white/10 p-8 max-w-md mx-auto">
              <div className="w-16 h-16 bg-gradient-to-br from-primary/20 to-primary/40 rounded-full mx-auto mb-4 flex items-center justify-center">
                <span className="text-2xl">📰</span>
              </div>
              <h3 className="text-2xl font-bold text-black mb-2">No Articles Yet</h3>
              <p className="text-black">Articles will be displayed here once they are added.</p>
            </div>
          </motion.div>
        )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Articles;
